import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, Check, X, ChevronRight, Loader2, AlertCircle, ArrowLeft } from 'lucide-react'

const ImportWorkout = ({ exercises = [], onImportComplete, onClose }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)

  // Steps: 'upload' -> 'mapping' -> 'confirming'
  const [step, setStep] = useState('upload')
  const [imagePreview, setImagePreview] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [parsedData, setParsedData] = useState(null)
  const [mappings, setMappings] = useState([])

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError(t('import.invalid_file'))
      return
    }

    setImageFile(file)
    setError(null)

    const reader = new FileReader()
    reader.onload = (e) => setImagePreview(e.target.result)
    reader.readAsDataURL(file)
  }, [t])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file && file.type.startsWith('image/')) {
      setImageFile(file)
      setError(null)
      const reader = new FileReader()
      reader.onload = (e) => setImagePreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }, [])

  const handleUpload = async () => {
    if (!imageFile) return

    setIsProcessing(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('image', imageFile)

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const res = await fetch('/api/v1/import_workouts/parse', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData
      })

      if (res.status === 429) {
        const data = await res.json()
        setIsProcessing(false)
        const message = `${data.message || '今月の画像丸投げ利用回数が上限に達しました'}\n\n現在のプラン: ${data.plan}\n利用: ${data.used} / ${data.limit}枚\n\n設定画面からアップグレードしてください。`
        setError(message)
        return
      }

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || t('error_occurred'))
      }

      // Poll for result
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await fetch('/api/v1/import_workouts/result')
          const data = await pollRes.json()

          if (data.status === 'completed') {
            clearInterval(pollInterval)
            setParsedData(data.data)
            initializeMappings(data.data)
            setStep('mapping')
            setIsProcessing(false)
          } else if (data.status === 'error') {
            clearInterval(pollInterval)
            setError(data.error)
            setIsProcessing(false)
          }
        } catch (e) {
          console.error('Polling error', e)
        }
      }, 2000)

      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval)
        if (isProcessing) {
          setIsProcessing(false)
          setError(t('import.timeout'))
        }
      }, 120000)

    } catch (e) {
      setError(e.message)
      setIsProcessing(false)
    }
  }

  const initializeMappings = (data) => {
    if (!data?.exercises) return

    const newMappings = data.exercises.map((ex) => ({
      originalName: ex.name,
      matchedId: ex.matched_exercise_id,
      matchedName: ex.matched_exercise_name,
      matchType: ex.match_type,
      bodyPart: ex.body_part || '',
      suggestions: ex.suggestions || [],
      sets: ex.sets || [],
      createNew: ex.match_type === 'none'
    }))

    setMappings(newMappings)
  }

  const updateMapping = (index, exerciseId, exerciseName) => {
    setMappings(prev => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        matchedId: exerciseId,
        matchedName: exerciseName,
        createNew: !exerciseId
      }
      return updated
    })
  }

  const handleConfirm = async () => {
    setIsProcessing(true)
    setError(null)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const workoutData = {
        date: parsedData.date,
        exercises: mappings.map(m => ({
          name: m.createNew ? m.originalName : m.matchedName,
          exercise_id: m.createNew ? null : m.matchedId,
          body_part: m.bodyPart,
          sets: m.sets
        }))
      }

      const res = await fetch('/api/v1/import_workouts/confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ workout_data: workoutData })
      })

      if (res.ok) {
        onImportComplete?.()
        onClose?.()
      } else {
        const data = await res.json()
        throw new Error(data.error || t('error_occurred'))
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto md:pt-10 pt-4">
      <div className="bg-[#1c1c1e] rounded-2xl border border-gray-800 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-[#1c1c1e] z-10">
          <div className="flex items-center gap-2">
            {step !== 'upload' && (
              <button
                onClick={() => setStep(step === 'confirming' ? 'mapping' : 'upload')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-lg font-bold text-white">{t('import.title')}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div>
              <p className="text-gray-400 text-sm mb-4">{t('import.description')}</p>

              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-700 hover:border-orange-500 rounded-xl p-8 text-center cursor-pointer transition-colors"
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-12 h-12 text-gray-500 mx-auto" />
                    <p className="text-gray-400">{t('import.drop_or_click')}</p>
                    <p className="text-gray-600 text-xs">{t('import.supported_formats')}</p>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              {imageFile && (
                <button
                  onClick={handleUpload}
                  disabled={isProcessing}
                  className="w-full mt-4 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-black font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('import.analyzing')}
                    </>
                  ) : (
                    <>
                      <ChevronRight className="w-5 h-5" />
                      {t('import.start_analysis')}
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Step 2: Mapping */}
          {step === 'mapping' && parsedData && (
            <div>
              <div className="mb-4 p-3 bg-[#2c2c2e] rounded-lg">
                <p className="text-sm text-gray-400">{t('import.detected_date')}</p>
                <p className="text-white font-bold">{parsedData.date}</p>
              </div>

              <p className="text-gray-400 text-sm mb-3">{t('import.mapping_description')}</p>

              <div className="space-y-3">
                {mappings.map((mapping, index) => (
                  <div key={index} className="bg-[#2c2c2e] rounded-lg p-3 border border-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium text-sm">{mapping.originalName}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${mapping.matchType === 'exact' ? 'bg-green-900/50 text-green-400' :
                        mapping.matchType === 'fuzzy' ? 'bg-yellow-900/50 text-yellow-400' :
                          'bg-red-900/50 text-red-400'
                        }`}>
                        {mapping.matchType === 'exact' ? '✅ ' + t('import.exact_match') :
                          mapping.matchType === 'fuzzy' ? '⚠️ ' + t('import.fuzzy_match') :
                            '❌ ' + t('import.no_match')}
                      </span>
                    </div>

                    {/* Sets preview */}
                    <div className="text-xs text-gray-500 mb-2">
                      {mapping.sets.map((s, si) => (
                        <span key={si} className="mr-2">
                          {s.weight}kg × {s.reps}{t('unit_reps')}
                        </span>
                      ))}
                    </div>

                    {/* Exercise selector */}
                    <div className="relative">
                      <label htmlFor={`exercise_select_${index}`} className="sr-only">{t('select_exercise')}</label>
                      <select
                        id={`exercise_select_${index}`}
                        value={mapping.matchedId || ''}
                        onChange={(e) => {
                          const id = parseInt(e.target.value)
                          if (id) {
                            const ex = exercises.find(ex => ex.id === id)
                            updateMapping(index, id, ex?.name)
                          } else {
                            updateMapping(index, null, null)
                          }
                        }}
                        className="w-full bg-[#3a3a3c] border border-gray-700 rounded-lg p-2 text-white text-sm focus:outline-none focus:border-orange-500 appearance-none"
                      >
                        <option value="">{t('import.create_new_exercise')}</option>
                        {/* Show suggestions first */}
                        {mapping.suggestions?.length > 0 && (
                          <optgroup label={t('import.suggestions')}>
                            {mapping.suggestions.map(s => (
                              <option key={s.id} value={s.id}>
                                {s.name} ({s.body_part})
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {/* All exercises */}
                        <optgroup label={t('import.all_exercises')}>
                          {exercises.map(ex => (
                            <option key={ex.id} value={ex.id}>
                              {ex.name} ({ex.body_part})
                            </option>
                          ))}
                        </optgroup>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-500">
                        <svg className="w-3 h-3 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path></svg>
                      </div>
                    </div>

                    {/* Body part selector for new exercises */}
                    {mapping.createNew && (
                      <div className="relative mt-2">
                        <label htmlFor={`body_part_select_${index}`} className="block text-gray-500 text-xs mb-1">{t('filter_by_part')}</label>
                        <select
                          id={`body_part_select_${index}`}
                          value={mapping.bodyPart || ''}
                          onChange={(e) => {
                            setMappings(prev => {
                              const updated = [...prev]
                              updated[index] = { ...updated[index], bodyPart: e.target.value }
                              return updated
                            })
                          }}
                          className="w-full bg-[#3a3a3c] border border-gray-700 rounded-lg p-2 text-white text-sm focus:outline-none focus:border-orange-500 appearance-none"
                        >
                          <option value="脚">{t('body_parts.legs')}</option>
                          <option value="腕">{t('body_parts.arms')}</option>
                          <option value="肩">{t('body_parts.shoulders')}</option>
                          <option value="背中">{t('body_parts.back')}</option>
                          <option value="胸">{t('body_parts.chest')}</option>
                          <option value="腹筋">{t('body_parts.abs')}</option>
                          <option value="有酸素運動">{t('body_parts.cardio')}</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-500" style={{ top: '20px' }}>
                          <svg className="w-3 h-3 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path></svg>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={handleConfirm}
                disabled={isProcessing}
                className="w-full mt-4 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('import.importing')}
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    {t('import.confirm_import')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ImportWorkout
